import { useState, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { usePlatformOrganisations } from '../hooks/usePlatformOrganisations';
import { PageHeader } from '../../../shared/components/layout/PageHeader';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';
import { PlatformOrganisationsListView } from '../components/organisms/PlatformOrganisationsListView';
import { CreateOrganisationDialog } from '../components/organisms/CreateOrganisationDialog';

type OrgTypeFilter = 'customers' | 'all' | 'e2e';

const ORG_TYPE_TABS = [
  { label: 'Customers', value: 'customers' },
  { label: 'All', value: 'all' },
  { label: 'E2E', value: 'e2e' },
] as const;

const isE2EOrg = (name: string) => name.startsWith('E2E');

const PlatformOrganisationsPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<OrgTypeFilter>('customers');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const {
    data: organisations,
    isLoading,
    error,
  } = usePlatformOrganisations(appliedSearch || undefined);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setAppliedSearch(value);
  };

  const filteredOrganisations = useMemo(() => {
    const orgs = organisations ?? [];
    if (typeFilter === 'all') return orgs;
    if (typeFilter === 'e2e') return orgs.filter(o => isE2EOrg(o.name));
    return orgs.filter(o => !isE2EOrg(o.name)); // customers
  }, [organisations, typeFilter]);

  if (isLoading && !organisations) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          p: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box>
          <PageHeader
            title="Organisations"
            actions={
              <Tooltip title="Create Organisation">
                <IconButton
                  size="small"
                  onClick={() => setCreateDialogOpen(true)}
                  data-testid="create-organisation-button"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            }
          />

          <PageToolbar
            tabs={
              <ChipTabs
                tabs={[...ORG_TYPE_TABS]}
                value={typeFilter}
                onChange={v => setTypeFilter(v as OrgTypeFilter)}
                data-testid="org-type-tabs"
              />
            }
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
          />

          <PlatformOrganisationsListView
            organisations={filteredOrganisations}
            loading={isLoading}
          />
        </Box>
      </Box>
      <CreateOrganisationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </Box>
  );
};

export default PlatformOrganisationsPage;
